// src/state/recruitingStore.js
// Single source of truth for Recruiting (shortlist) state.
// Uses the REAL API routes (not the /debug/* helpers).

import { create } from 'zustand';

const API_BASE = '/api/teams/recruiting';
const TEAM_META_ENDPOINT = `${API_BASE}/team-meta`;
const _teamMetaCache = new Map(); // team_id -> { team:{...}, captain:{...} }


export const useRecruitingStore = create((set, get) => ({
  // ---------- State ----------
  captainId: null,                 // number | null
  teamId: null,
  listId: null,                    // number | null
  items: [],                       // array of recruiting_list_item rows
  loading: false,
  error: null,

  filters: {
    focus: null,                   // 'B2B' | 'B2C' | 'B2BC' | null
    minRating: null,               // number | null
    distanceMiles: null,           // number | null
    industry: null                 // string | null
  },

  // ---------- Team meta cache (captain + team) ----------
  // Shape: { [team_id:number]: { team:{team_id, team_name, owner_u_id}, captain:{u_id, first_name, last_name, full_name, u_profile_picture} } }
  teamMetaById: {},


  // ---------- Actions ----------
  setCaptainId: (uId) => set({ captainId: Number(uId) || null }),
  setTeamId: (tId) => set({ teamId: Number(tId) || null }),

  setFilters: (patch) =>
    set((s) => ({ filters: { ...s.filters, ...(patch || {}) } })),

  resetError: () => set({ error: null }),

  // ---------- Team meta cache actions ----------
  setTeamMeta: (teamId, meta) =>
    set((s) => ({
      teamMetaById: { ...s.teamMetaById, [Number(teamId)]: meta }
    })),

  getTeamMeta: (teamId) => {
    const m = get().teamMetaById || {};
    return m[Number(teamId)] || null;
  },

  // Loads { team, captain } for a given team once, caches by team_id.
  // Uses the new endpoint: GET /api/teams/recruiting/team-meta?team_id=123
  loadTeamMeta: async (teamIdArg) => {
    const tid = Number(
      teamIdArg != null ? teamIdArg : get().teamId
    );
    if (!Number.isFinite(tid)) return null;

    // 1) Return cache if present
    const cached = get().getTeamMeta(tid);
    if (cached) return cached;

    // 2) Fetch and cache
    try {
      const res = await fetch(`${API_BASE}/team-meta?team_id=${tid}`, { credentials: 'include' });
      if (!res.ok) return null;
      const json = await res.json(); // { team:{...}, captain:{...} }
      get().setTeamMeta(tid, json);
      return json;
    } catch (e) {
      console.error('[recruitingStore.loadTeamMeta] error:', e);
      return null;
    }
  },


  // Load (or create) the captain's current recruiting list, then load items.
  // You must pass a valid numeric captainId the first time.
  loadList: async (captainIdArg) => {
    const state = get();
    const captainId = Number(
      captainIdArg != null ? captainIdArg : state.captainId
    );
    if (!Number.isFinite(captainId)) {
      set({ error: 'loadList: captainId is required' });
      return;
    }
    set({ loading: true, error: null, captainId });

    try {
      // 1) Read the list (may be null on first run)
      const readRes = await fetch(
        `${API_BASE}/list?captain_u_id=${captainId}`
      );
      if (!readRes.ok) {
        throw new Error(`GET /list ${readRes.status}`);
      }
      const readJson = await readRes.json();

      // 2) If no list yet, create one and re-read
      let listId = readJson?.list?.recruiting_list_id ?? null;
      if (!listId) {
        const createRes = await fetch(`${API_BASE}/list`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ captain_u_id: captainId })
        });
        if (!createRes.ok) {
          throw new Error(`POST /list ${createRes.status}`);
        }
        const createJson = await createRes.json();
        listId = createJson?.list?.recruiting_list_id;
        if (!listId) throw new Error('No list id returned');
      }

      // 3) Re-read to get items (keeps one code path)
      const finalRes = await fetch(
        `${API_BASE}/list?captain_u_id=${captainId}`
      );
      if (!finalRes.ok) {
        throw new Error(`GET /list (final) ${finalRes.status}`);
      }
      const finalJson = await finalRes.json();

      set({
        listId: finalJson?.list?.recruiting_list_id ?? listId,
        items: finalJson?.items ?? [],
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('[recruitingStore.loadList] error:', err);
      set({ loading: false, error: err?.message || 'Failed to load list' });
    }
  },

  // Load a team's master recruiting list (team-scoped; does NOT create).
  loadTeamList: async (teamIdArg) => {
    const state = get();
    const teamId = Number(
      teamIdArg != null ? teamIdArg : state.teamId
    );
    if (!Number.isFinite(teamId)) {
      set({ error: 'loadTeamList: teamId is required' });
      return;
    }

    set({ loading: true, error: null, teamId });

    try {
      const res = await fetch(`/api/teams/recruiting/list?team_id=${teamId}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`GET /list?team_id ${res.status}`);

      const json = await res.json(); // { list, items }
      set({
        listId: json?.list?.recruiting_list_id ?? null,
        items: json?.items ?? [],
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('[recruitingStore.loadTeamList] error:', err);
      set({ loading: false, error: err?.message || 'Failed to load team list' });
    }
  },


  // Add one or more candidates by user id(s).
  addCandidates: async (candidateIds) => {
    const { listId, captainId } = get();
    const ids = Array.isArray(candidateIds) ? candidateIds : [candidateIds];
    if (!listId && !captainId) {
      set({ error: 'addCandidates: listId or captainId is required' });
      return;
    }
    if (!ids.length) return;

    set({ loading: true, error: null });
    try {
      const body = listId
        ? { recruiting_list_id: Number(listId), candidate_u_ids: ids.map(Number) }
        : { captain_u_id: Number(captainId), candidate_u_ids: ids.map(Number) };

      const res = await fetch(`${API_BASE}/items`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`POST /items ${res.status}`);

      const json = await res.json();
      set({
        listId: json?.recruiting_list_id ?? listId,
        items: json?.items ?? [],
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('[recruitingStore.addCandidates] error:', err);
      set({ loading: false, error: err?.message || 'Failed to add candidates' });
    }
  },

  // Update an item (status, notes, tags). Accepts a full row or {id, patch}.
  updateItem: async (itemId, patch) => {
    const id = Number(itemId);
    if (!Number.isFinite(id)) return;

    set({ loading: true, error: null });
    try {
      const res = await fetch(`${API_BASE}/items/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch || {})
      });
      if (!res.ok) throw new Error(`PATCH /items/:id ${res.status}`);

      const updated = await res.json();
      set((s) => ({
        items: s.items.map((it) =>
          Number(it.recruiting_list_item_id) === id ? updated : it
        ),
        loading: false,
        error: null
      }));
    } catch (err) {
      console.error('[recruitingStore.updateItem] error:', err);
      set({ loading: false, error: err?.message || 'Failed to update item' });
    }
  },

  // Remove an item from the shortlist.
  removeItem: async (itemId) => {
    const id = Number(itemId);
    if (!Number.isFinite(id)) return;

    set({ loading: true, error: null });
    try {
      const res = await fetch(`${API_BASE}/items/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.status !== 204) throw new Error(`DELETE /items/:id ${res.status}`);

      set((s) => ({
        items: s.items.filter(
          (it) => Number(it.recruiting_list_item_id) !== id
        ),
        loading: false,
        error: null
      }));
    } catch (err) {
      console.error('[recruitingStore.removeItem] error:', err);
      set({ loading: false, error: err?.message || 'Failed to remove item' });
    }
  }
}));
